/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable max-lines */
import {
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type {
  ChatAppRequest,
  ChatAppResponse,
  ChatAppResponseOrError,
  ChatSessionModel,
  DurableChatTurnIdentity,
  DurableChatTurnMode,
  GraphFileItem,
} from "@/api/models"
import type { ChatSlice, SessionState } from "@/stores/slices/chat-slice"
import type { StoreApi, UseBoundStore } from "zustand"

import {
  acceptDurableChatTurnApi,
  cancelDurableChatTurnApi,
  cancelQueueRequestApi,
  downloadGraphFileApi,
} from "@/api/api"
import { ApiError } from "@/api/client/_shared"
import { useRoutes } from "@/contexts/use-routes"
import { useBaseSettings } from "@/features/base-settings/hooks/use-base-settings"
import { useStreamHandler } from "@/features/chat/hooks/use-stream-handler"
import {
  isDurableChatTurnModeEnabled,
  isDurableExplorerSendEligible,
  shouldFallbackToLegacyTransport,
} from "@/features/chat/utils/durable-chat-turn-mode"
import { routeFairUseError, routeFairUseMetadata } from "@/features/fair-use/fair-use-ws-bridge"
import { useConfigQuery } from "@/hooks/use-config-query"
import { useFeatureEnabled } from "@/hooks/use-feature-enabled"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"
import { ChatService } from "@/services/chat-service"
import { SettingsService } from "@/services/settings-service"
import { defaultSessionState } from "@/stores/slices/chat-slice"
import { useWorkflowRunStore } from "@/stores/workflow-run-store"
import { getResponseContent, isLegacyFormat } from "@/utils/response-format-utils"

import {
  type AbortedRequest,
  beginChatRequest,
  type ChatRequestControl,
  chatRequestOwnsSession,
  finishChatRequestControl,
  getChatRequestAborted,
  getChatRequestControl,
  getChatRequestOwnerToken,
  getChatRequestSessionDisposalGeneration,
  isChatRequestSessionDisposalGenerationCurrent,
  isChatRequestSessionDisposed,
  retireChatRequest,
  setChatRequestAborted,
  subscribeChatRequestRegistry,
  writeChatRequestState,
} from "./chat-request-registry"
import { matchesTarget } from "./durable-chat-turn-registry-utils"
import { generateDurableChatTurnId } from "./durable-chat-turn-state"
import {
  registerDurableSessionPlaceholder,
  releaseDurableSessionPlaceholder,
} from "./durable-session-placeholders"
import { useBackgroundChatCompletion } from "./use-background-chat-completion"
import { useChatSessionSyncPublisher } from "./use-chat-session-cross-tab-sync"
import {
  attachDurableChatTurnToSession,
  useDurableChatTurnHydration,
} from "./use-durable-chat-turn-hydration"
import { useSaveGuardWatchdog } from "./use-save-guard-watchdog"
import { useStartNewChat } from "./use-start-new-chat"
import {
  clearSteerComposerText,
  getSteerComposerSend,
  peekSteerComposerText,
  restoreSteerComposerText,
  sendSteerControlFrame,
  useSteerFeatureToggles,
} from "./use-steer-actions"
import {
  getLastQuestionForRequest,
  getRetryRequestForLastQuestion,
} from "../utils/chat-request-utils"
import { settleSteerNotesAtTurnEnd } from "../utils/steer-note-settlement"

// Steer stage-2 stop (issue #2438): how long to wait for the server's
// cancel_ack before falling back to the legacy hard abort, and — once acked —
// how long to allow for the wind-down (server save budget is 60s; the margin
// covers response_complete → message_saved delivery) before giving up.
const STEER_CANCEL_ACK_TIMEOUT_MS = 5_000
const STEER_CANCEL_WINDDOWN_TIMEOUT_MS = 70_000
// A stashed stop-and-redirect draft older than this never auto-fires — it is
// restored to the composer instead (a redirect can otherwise fire hours later
// when the user revisits a session whose cancel never fully settled).
const STEER_REDIRECT_MAX_AGE_MS = 80_000

type ModuleRoute = {
  id: string
  overrides?: {
    // A route may map to a session type distinct from its id (HERMES: the
    // "agents" route uses the "hosted-agent" session type). See `chatSessionType`.
    overrideSessionType?: string
  }
}

type RequestStateWriter = (partial: Partial<SessionState>) => boolean

type UseChatProps = {
  moduleRoute: ModuleRoute
  useChatStore: () => UseBoundStore<StoreApi<ChatSlice>>
  getChatResponse?: ({
    question,
    abortController,
    isContinuation,
    startResearch,
    agentId,
    forceWebSearch,
    files,
    userInputData,
    requestToken,
  }: {
    question: string
    abortController: AbortController
    isContinuation?: boolean
    isRetry?: boolean
    startResearch?: boolean
    agentId?: string
    forceWebSearch?: boolean
    files?: File[]
    /**
     * Text (string) START-field values keyed by field key, carried alongside a
     * workflow run so the backend can seed context.user_input_data. Only text
     * fields ride here; file fields go through the file-upload pipeline.
     */
    userInputData?: Record<string, string>
    requestToken: symbol
  }) => Promise<Response>
  getCustomChatResponse?: ({
    question,
    videoId,
    isContinuation,
    selectedLanguages,
  }: {
    question: string
    videoId?: string
    isContinuation?: boolean
    selectedLanguages?: string[]
  }) => Promise<void>
  projectId?: string
  /**
   * Durable turns (issue #2465): resolves which durable mode this send
   * would use, given the same per-call flags `sendChatRequest` receives
   * (currently only `startResearch` — Agent Mode / Deep are read live from
   * the caller's own store since they aren't per-call params here). Return
   * `null` to keep this send on the legacy transport unconditionally (e.g.
   * Deep Explorer). Only callers that pass this AND `buildDurableChatTurnRequest`
   * can ever take the durable path — every other caller (Translator, Video
   * Insight, Dash) is structurally unreachable, not just flag-gated off.
   */
  resolveDurableChatTurnMode?: (params: { startResearch?: boolean }) => DurableChatTurnMode | null
  /**
   * Builds the `ChatAppRequest` payload for a durable-turn HTTP acceptance.
   * Only invoked when `resolveDurableChatTurnMode` returns a mode that is
   * both `"explorer"` and enabled via `durable_chat_turns.config.enabled_modes`.
   * `turnId` is the pre-submit identity generated before any transport call —
   * callers must stamp it nowhere themselves; `acceptDurableChatTurnApi`
   * stamps `request.turn_id` from the sibling `turnId` field.
   */
  buildDurableChatTurnRequest?: (params: {
    forceWebSearch?: boolean
    isRetry: boolean
    question: string
    requestFiles: File[] | undefined
    turnId: string
    userInputData?: Record<string, string>
  }) => Promise<ChatAppRequest>
  onDurableChatTurnAccepted?: (request: ChatAppRequest) => void
}

export function useChat({
  moduleRoute,
  useChatStore,
  getChatResponse,
  getCustomChatResponse,
  projectId,
  resolveDurableChatTurnMode,
  buildDurableChatTurnRequest,
  onDurableChatTurnAccepted,
}: UseChatProps) {
  const { t } = useTranslation()
  const { getRouteChatPath } = useRoutes()
  const { data: config } = useConfigQuery()

  // Session type vs route id: most modules use their route id as the session
  // type, but a route may declare a distinct `overrideSessionType` (HERMES:
  // the "agents" route maps to the "hosted-agent" session type, matching
  // `useCurrentSessionType`). Session-scoped keys (create/open/list sessions,
  // history, the hosted-agent binding lookup) must use this resolved type so
  // the binding is found and the durable turn reaches the hosted-agent run
  // queue; URLs keep using `moduleRoute.id`. For every non-agent module the
  // two are identical, so this is a no-op there.
  const chatSessionType = moduleRoute.overrides?.overrideSessionType ?? moduleRoute.id

  // Get DB-backed settings for streaming and model
  const { gptModel, shouldStream } = useBaseSettings({
    sessionType: chatSessionType,
  })

  const [chatService] = useState<ChatService>(ChatService.getInstance())
  const [settingsService] = useState<SettingsService>(SettingsService.getInstance())
  const queryClient = useQueryClient()

  const chatStore = useChatStore()
  const currentSessionId = chatStore((state) => state.currentSessionId)
  const chatState = chatStore((state) => {
    const sessionId = state.currentSessionId
    return sessionId ? state.sessionStates[sessionId] || defaultSessionState : defaultSessionState
  })
  const changeChatState = chatStore((state) => state.changeChatState)
  const files = chatStore((state) => state.files)
  const addFiles = chatStore((state) => state.addFiles)
  const resetFiles = chatStore((state) => state.resetFiles)
  const removeFile = chatStore((state) => state.removeFile)
  const setCurrentSessionId = chatStore((state) => state.setCurrentSessionId)
  const totalTokenCount = chatState.tokenCounts.text + chatState.tokenCounts.files
  const changeTokenCounts = chatStore((state) => state.changeTokenCounts)
  const getActiveSessionId = () => chatStore.getState().currentSessionId

  // Durable turn hydration (issue #2513): opening a chat that already has an
  // in-flight durable turn — a second tab, another device, or this same tab
  // reopened after a full close — must attach to it and follow it live,
  // without restarting execution or duplicating the final message. Gated on
  // the exact same structural signal the durable SEND path uses
  // (`buildDurableChatTurnRequest !== undefined`, wired only for the bare,
  // no-`options` Explorer instance — see use-ai-explorer-chat.ts;
  // Dash/Translator/Scholar never pass it) so hydration can never fire for a
  // caller that could never have created a durable turn in the first place.
  //
  // Deliberately does NOT also gate on `resolveDurableChatTurnMode(...)`
  // resolving to `"explorer"` right now: that reflects the composer's
  // CURRENT Deep/Agent toggle state, which has no bearing on whether an
  // ALREADY-ACCEPTED turn — always mode "explorer", see
  // `sendDurableExplorerChatTurn`'s hardcoded `mode: "explorer"` below — should
  // be followed. A viewer who has since flipped Agent/Deep on must still see
  // a still-running plain-Explorer turn. Likewise omits the send gate's
  // continuation/fresh-upload exclusions (`isDurableExplorerSendEligible`):
  // those describe what a NEW send is allowed to do, not whether an
  // already-accepted turn is followable.
  // Stable identity across renders — see `useDurableChatTurnHydration`'s own
  // doc comment on why the hydration effect's deps must not churn with this
  // accessor (issue #2513 finding 1). `settingsService` is itself a stable
  // singleton instance (`useState(() => SettingsService.getInstance())`
  // above), so this only changes if that singleton reference ever did.
  const getDurableToken = useCallback(() => settingsService.token, [settingsService])
  // Cross-window sync (issue #2548). Same `sessionType`/`projectId` scope as
  // every `queryKeys.sessionHistory` write in this hook, so a hint only ever
  // refreshes the list it actually belongs to. A strict no-op while the
  // `cross_window_session_sync` flag is off.
  const publishSessionChange = useChatSessionSyncPublisher({
    sessionType: chatSessionType,
    projectId,
  })
  // Issue #2469: leftover pending steers at turn-end requeue only when the
  // queue can auto-drain — same gate as the WS undelivered fallback.
  const isMessageQueueEnabled = useFeatureEnabled("explorer_message_queue")
  useDurableChatTurnHydration({
    chatStore,
    isMessageQueueEnabled,
    enabled:
      buildDurableChatTurnRequest !== undefined &&
      isDurableChatTurnModeEnabled(config?.features, "explorer"),
    getToken: getDurableToken,
    // Same sidebar cover the send path registers below, for the tab that did
    // NOT send: a mid-turn reload (or a second tab) has no optimistic cache
    // write to inherit, and the server's list still reports the in-flight
    // chat untitled until the turn completes — so without this the chat is
    // filtered out of the sidebar for the rest of the turn.
    placeholderTitle: t("chatsession.new_chat", { defaultValue: "New chat" }),
    // A durable session is only NAMED when the turn completes, so the
    // terminal transition is the moment the other windows' sidebars have
    // something new to show (issue #2548). Fires once per attach, never per
    // streamed frame.
    onSessionListSettled: () => publishSessionChange("renamed"),
    // Threaded through to `attachDurableChatTurnToSession` so a passive
    // viewer (hydrating a turn it didn't send) gets the same session-list
    // delivery the sender gets below in `sendDurableExplorerChatTurn` —
    // reuses the identical `queryKeys.sessionHistory` scope as this hook's
    // own accept-time invalidate (issue #2513).
    projectId,
    queryClient,
    sessionId: currentSessionId,
    sessionType: chatSessionType,
  })

  const subscribeRequestRegistry = useCallback(
    (listener: () => void) => subscribeChatRequestRegistry(chatStore, listener),
    [chatStore]
  )
  const getAbortedSnapshot = useCallback(
    () => getChatRequestAborted(chatStore, currentSessionId),
    [chatStore, currentSessionId]
  )
  const aborted = useSyncExternalStore(
    subscribeRequestRegistry,
    getAbortedSnapshot,
    getAbortedSnapshot
  )

  const setAbortedForSession = useCallback(
    (sessionId: string | null, value: SetStateAction<AbortedRequest | undefined>) => {
      if (sessionId) setChatRequestAborted({ store: chatStore, sessionId }, value)
    },
    [chatStore]
  )
  const setAborted = useCallback(
    (value: SetStateAction<AbortedRequest | undefined>) => {
      setAbortedForSession(chatStore.getState().currentSessionId, value)
    },
    [chatStore, setAbortedForSession]
  )

  // Race-free backstop: release the composer if isAwaitingSave gets stuck after a
  // turn settles without a `message_saved` ack (ServiceDesk 130507). Also wires
  // the R6 re-verify token so the durable-turn reconnect guard can confirm a
  // stuck turn is actually settled before releasing it.
  useSaveGuardWatchdog(chatStore, undefined, undefined, getDurableToken)

  // Steer stage-2 stop (issue #2438). `stopRedirectEnabled` is false whenever
  // the explorer_steer flag is off, so the entire cancel-frame path below is
  // unreachable and Stop keeps today's abort behavior byte-for-byte.
  const { stopRedirectEnabled: steerStopRedirectEnabled } = useSteerFeatureToggles()
  // Cancel watchdog timers keyed per session — two sessions can have cancels
  // in flight at once, and arming one must not disarm the other's fallback.
  const steerCancelTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const clearSteerCancelWatchdog = (sessionId: string) => {
    const timer = steerCancelTimersRef.current.get(sessionId)
    if (timer != null) {
      clearTimeout(timer)
      steerCancelTimersRef.current.delete(sessionId)
    }
  }

  useEffect(() => {
    const timers = steerCancelTimersRef.current
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer)
      }
      timers.clear()
    }
  }, [])

  const { handleStartNewChat } = useStartNewChat({
    useChatStore,
    sessionType: chatSessionType,
  })
  const navigate = useNavigate()
  const notifyBackgroundChatCompletion = useBackgroundChatCompletion({
    chatStore,
    fallbackSessionType: chatSessionType,
    getRouteChatPath,
  })

  const path = useLocation()

  // Only auto-start new chat if we're on a chat route without a session ID
  // Don't trigger on base routes like /custom-variant or /
  useEffect(() => {
    const isOnChatRoute = path.pathname.includes("/chat/")
    const hasSessionId = path.pathname.match(/\/chat\/[^/]+/)

    // If we're on a chat route but don't have a session ID, start a new chat
    if (isOnChatRoute && !hasSessionId) {
      handleStartNewChat()
    }
  }, [path.pathname])

  const { handleStreamRequest } = useStreamHandler()

  const initializeRequest = ({
    question,
    isContinuation,
    isRetry,
    requestFiles,
    requestAgentId,
    writeRequestState,
  }: {
    question: string
    isContinuation: boolean
    isRetry: boolean
    requestFiles: File[] | undefined
    requestAgentId: string | undefined
    writeRequestState: RequestStateWriter
  }) => {
    writeRequestState({
      error: undefined,
      canContinue: false,
      isLoading: !(shouldStream && isContinuation),
      isAwaitingSave: true,
      // A new turn starts with a clean steer slate (issue #2438); steer_ready
      // for this turn arrives over the WS once the tool loop is live.
      steerReady: false,
      steerNotes: [],
      cancelPending: false,
      cancelAckReceived: false,
      pendingRedirect: null,
      sawMessageSaved: false,
    })

    if (!isContinuation) {
      const initialResponse: ChatAppResponse = {
        content: "",
        context: { data_points: [], followup_questions: null, thoughts: [] },
        session_state: null,
        completed: false,
      }
      if (!isRetry) {
        writeRequestState({
          lastQuestion: getLastQuestionForRequest(question, files, requestFiles),
          lastRequestAgentId: requestAgentId,
        })
      }
      return initialResponse
    }
    return undefined
  }

  // Write partial state to the session that ORIGINATED the request, not whichever
  // session happens to be open when the write lands. Mirrors the sessionId
  // threading in the streaming path so a mid-flight session switch can't clobber
  // the wrong chat. Request code refuses to start without a concrete session.
  const writeSessionState = (sessionId: string, partial: Partial<SessionState>) => {
    chatStore.getState().updateSession(sessionId, partial)
  }

  // Read the ORIGINATING session's live state from the store (not the rendering
  // closure) so message appends target the right message list.
  const readSessionState = (sessionId: string): SessionState => {
    return chatStore.getState().sessionStates[sessionId] ?? defaultSessionState
  }

  const handleNonStreamingResponse = async ({
    response,
    question,
    isContinuation,
    sessionId,
    writeRequestState,
  }: {
    response: Response
    question: string
    isContinuation: boolean
    sessionId: string
    writeRequestState: RequestStateWriter
  }) => {
    const jsonResponse: ChatAppResponseOrError = await getValidResponse(response)

    const parsedResponse = jsonResponse as ChatAppResponse
    parsedResponse.completed = true
    // Fair use (#2625): the non-streaming REST reply carries the same
    // `context.fair_use` block the WS `done` frame does, and this path never
    // reaches `use-websocket-message-handler.ts` — so without this the
    // composer's usage ring stayed at whatever the session-start snapshot
    // said until the next reload.
    routeFairUseMetadata(queryClient, { context: parsedResponse.context })
    const interaction: [string, ChatAppResponse] = [question, parsedResponse]

    const originatingState = readSessionState(sessionId)
    writeRequestState({
      messages: isContinuation
        ? [...originatingState.messages.slice(0, -1), interaction]
        : [...originatingState.messages, interaction],
      streamingAnswer: null,
      isLoading: false,
      canContinue: false,
    })

    return parsedResponse
  }

  const getValidResponse = async (response: Response) => {
    let jsonResponse: ChatAppResponseOrError
    try {
      jsonResponse = await response.json()
      // If response status ok, but has error in response, throw error
      if ("error" in jsonResponse) {
        const errorMessage = `HTTP error! status: ${response.status} \n${jsonResponse.error}`
        throw new Error(errorMessage)
      }

      // Validate response structure - handle both new and legacy formats
      const hasValidNewFormat =
        "content" in jsonResponse && typeof jsonResponse.content === "string"
      const hasValidLegacyFormat = isLegacyFormat(jsonResponse)

      if (!hasValidNewFormat && !hasValidLegacyFormat) {
        throw Error("Invalid response format")
      }

      return jsonResponse
    } catch {
      throw new Error(
        `HTTP error! status: ${response.status}\n${response.statusText}. \nCould not parse error message`
      )
    }
  }

  const handleRequestError = (
    error: unknown,
    request: {
      sessionId: string
      requestToken: symbol
      parsedResponse?: ChatAppResponse
    }
  ) => {
    const { parsedResponse, requestToken, sessionId } = request
    // A superseded request must not overwrite the newer turn's flags or abort UI.
    if (!chatRequestOwnsSession(chatStore, sessionId, requestToken)) return

    if (error instanceof Error && error.name === "AbortError") {
      setChatRequestAborted({ store: chatStore, sessionId, requestToken }, [
        readSessionState(sessionId).lastQuestion[0],
        undefined,
      ])
      writeChatRequestState(
        { store: chatStore, sessionId, requestToken },
        {
          error: undefined,
          canContinue: true,
          isStreaming: false,
          isLoading: false,
          isAwaitingSave: false,
        }
      )
    } else {
      writeChatRequestState(
        { store: chatStore, sessionId, requestToken },
        {
          error,
          canContinue: true,
          isStreaming: false,
          isLoading: false,
          isAwaitingSave: false,
        }
      )
    }
    if (parsedResponse) {
      parsedResponse.completed = false
    }
  }

  /**
   * Durable-turn send path (issue #2465, Explorer only). Deliberately
   * independent of the legacy `getChatResponse` → `handleStreamRequest`
   * machinery below: that machinery ties completion/error handling to
   * `abortController.signal`, and Stop/unmount/navigate must NEVER cancel a
   * durable turn's server-side execution. The `AbortController` created here
   * exists only to satisfy `beginChatRequest`'s stale-write-protection
   * bookkeeping (a newer send superseding an older one's local writes) — its
   * `.signal` is never read by anything, so aborting it is inert for the
   * turn itself.
   *
   * HTTP acceptance always precedes the subscription: `acceptDurableChatTurnApi`
   * is awaited before `attachDurableChatTurnToSession` ever registers with the
   * registry (which is what starts the WS subscription).
   *
   * Returns `"fallback-to-legacy"` (issue #2513) when the accept call fails
   * with one of the narrow signals meaning "durable transport is not
   * available here" (see `shouldFallbackToLegacyTransport`) — e.g. the
   * frontend's durable gate is open but the backend's is not
   * (`403 durable_turns_disabled`). In that case NO durable-side state has
   * been created yet (the accept call itself failed, so the server never
   * created a turn; `attachDurableChatTurnToSession` — the only thing that
   * registers with the durable registry — is never reached on this path),
   * so the caller can safely retry the exact same turn on the legacy
   * transport with no unwind and no risk of a double send. Every other
   * outcome returns `"handled"` and the caller must not do anything further
   * for this turn.
   *
   * Double-attach guard (durable activity-latency review, finding 2): this
   * function re-checks `sessionStates[…].durableTurn` immediately after the
   * accept round-trip and skips its own `attachDurableChatTurnToSession`
   * call if the passive-viewer poll already attached the same turn while
   * the accept was in flight — see the comment at that call site. The
   * finding's alternative — making `attachDurableChatTurnToSession` itself
   * idempotent per (session, epoch, turn), e.g. via a module-level
   * refcounted registry of active attaches — was considered and rejected
   * for now: every current call site (this one, both hydration-hook attach
   * paths, and the majority of this function's own unit tests) treats the
   * returned unsubscribe as page-lifetime and freely discards it, so a
   * refcounted scheme would need a second, parallel piece of state that
   * tracks "how many live callers still hold a handle to this identity" —
   * state nothing resets between test cases today, unlike
   * `durableChatTurnRegistry`, which every suite explicitly clears. That
   * makes it a real design change (call-site auditing, a new reset hook for
   * tests) rather than a bounded fix, for a hazard that — with this
   * call-site guard plus the poll's existing symmetric check — has no
   * remaining unguarded ordering: sender-first is covered by the poll's own
   * live-store re-check, and poll-first is now covered by this one.
   */
  const sendDurableExplorerChatTurn = async ({
    forceWebSearch,
    isRetry,
    question,
    requestAgentId,
    requestFiles,
    sessionIdForRequest,
    userInputData,
  }: {
    forceWebSearch?: boolean
    isRetry: boolean
    question: string
    requestAgentId: string | undefined
    requestFiles: File[] | undefined
    sessionIdForRequest: string
    userInputData?: Record<string, string>
  }): Promise<"handled" | "fallback-to-legacy"> => {
    const existingDurableTurn = readSessionState(sessionIdForRequest).durableTurn
    if (existingDurableTurn) {
      logger.warn("Durable chat turn send ignored — a turn is already active for this session", {
        sessionId: sessionIdForRequest,
        turnId: existingDurableTurn.turnId,
      })
      return "handled"
    }

    const abortController = new AbortController()
    const { requestToken } = beginChatRequest(chatStore, sessionIdForRequest, abortController)
    const writeRequestState: RequestStateWriter = (partial) =>
      writeChatRequestState(
        { store: chatStore, sessionId: sessionIdForRequest, requestToken },
        partial
      )

    initializeRequest({
      question,
      isContinuation: false,
      isRetry,
      requestFiles,
      requestAgentId,
      writeRequestState,
    })

    // Stable pre-submit identity: generated before any transport call so it
    // survives the HTTP accept, every subscription frame, and a later
    // history reload unchanged.
    const turnId = generateDurableChatTurnId()

    // Keep a brand-new chat in the sidebar for the WHOLE durable turn. The
    // optimistic `{ title: "New chat" }` row `ensureSession` /
    // `handleSendQuestion` wrote into `queryKeys.sessionHistory` is a one-shot
    // cache write: a durable session stays untitled server-side until the turn
    // completes (`ChatTurnRunner._complete` → `_enrich_and_publish_session`),
    // so the first refetch that lands mid-turn — window focus, a 30s-stale
    // remount, another module's invalidate — replaces it with `title: ""` and
    // the sidebar's `sessions.filter((s) => s?.title)` drops the chat until the
    // user reloads. Legacy never needs this: it titles the session mid-stream
    // and pushes the real title over `title_state_changed`. Registered BEFORE
    // the accept round-trip so a refetch already in flight is covered too, and
    // released when the turn settles (see `attachDurableChatTurnToSession`).
    registerDurableSessionPlaceholder(
      sessionIdForRequest,
      turnId,
      t("chatsession.new_chat", { defaultValue: "New chat" })
    )
    // Single owner for the placeholder registered above (L1 fix, durable
    // activity-latency review round 2): every early return between here and
    // the bottom of this function must either hand the placeholder off to an
    // attach's own release lifecycle (setting this `true`) or fall through to
    // the `finally` below, which releases it. This makes "forgot to release
    // on a new early-return" structurally impossible instead of relying on
    // every call site to remember its own `releaseDurableSessionPlaceholder`
    // call — a superseded (by a newer send) or stopped (by Stop) accept that
    // never reaches an attach must not leave the module-level placeholder
    // registered for the rest of the page's life.
    let placeholderHandedToAttach = false

    try {
      const request = await buildDurableChatTurnRequest!({
        forceWebSearch,
        isRetry,
        question,
        requestFiles,
        turnId,
        userInputData,
      })
      const accepted = await acceptDurableChatTurnApi(
        sessionIdForRequest,
        { turnId, mode: "explorer", request },
        settingsService.token
      )
      onDurableChatTurnAccepted?.(request)
      // A newer send (or Stop) may have superseded this one while the accept
      // request was in flight — a superseded acceptance must not attach its
      // subscription and start writing over a newer turn's state. Falls
      // through to the `finally` below, which releases the placeholder this
      // function registered above (`placeholderHandedToAttach` is still
      // `false`) — nothing else will, since we never reached an attach.
      if (!chatRequestOwnsSession(chatStore, sessionIdForRequest, requestToken)) {
        // The server already committed/scheduled this exact turn — losing
        // ownership here (Stop retiring `requestToken` while this accept was
        // in flight, or a newer send superseding it) must not leave it
        // running orphaned: nothing else has this identity, so nothing else
        // will ever call cancel for it, and hydration can rediscover and
        // keep streaming it after Stop already told the user it stopped.
        // `accepted.turnId` is freshly minted per send, so this can never
        // race a legitimate newer owner's own turn. Best-effort: a failed
        // cancel here must not surface as this send's own error.
        try {
          await cancelDurableChatTurnApi(
            accepted.sessionId,
            { turnId: accepted.turnId, reason: "deferred_accept_abandoned" },
            settingsService.token
          )
        } catch (error) {
          logger.error(
            "Failed to cancel a deferred-accept turn that lost ownership",
            error as Error
          )
        }
        return "handled"
      }

      const identity: DurableChatTurnIdentity = {
        sessionId: accepted.sessionId,
        turnId: accepted.turnId,
        epoch: accepted.epoch,
      }
      // Re-check for an already-attached turn, taken AFTER the accept
      // round-trip and immediately before attaching (durable activity-
      // latency review, finding 2). `existingDurableTurn` above was read
      // BEFORE this `await` — it only catches "another attacher went first,
      // entirely before we even started." It does NOT catch the reverse:
      // the passive-viewer poll (`useDurableChatTurnHydration`'s poll
      // effect) discovering and attaching this SAME turn while THIS accept
      // call was still in flight, since nothing here re-checked after the
      // `await` resumed. Both paths read/write the exact same
      // `sessionStates[sessionId].durableTurn` field, and
      // `attachDurableChatTurnToSession` performs its store write
      // synchronously with no `await` of its own — so a synchronous re-read
      // here reliably observes a winner that already ran to completion,
      // with no partial-execution window to race against. Skip our own
      // attach rather than double-subscribing; the other attacher's mirror
      // already covers this session for the same turn.
      const alreadyAttached = readSessionState(sessionIdForRequest).durableTurn
      if (alreadyAttached && matchesTarget(alreadyAttached, identity)) {
        // The other attacher's own placeholder registration (same session id,
        // same key) already covers this turn and will release on its first
        // terminal emit — hand off rather than releasing here, which would
        // otherwise yank the cover out from under it.
        placeholderHandedToAttach = true
        return "handled"
      }
      // From here on, `attachDurableChatTurnToSession`'s registry mirror is
      // the authoritative writer for this session's turn state — page-lifetime,
      // independent of this request's ownership token (matches the registry's
      // own contract: listener lifetime is a UI concern only).
      placeholderHandedToAttach = true
      attachDurableChatTurnToSession({
        chatStore,
        isMessageQueueEnabled,
        getToken: () => settingsService.token,
        identity,
        // Hands the cover registered above to the attach's own lifecycle: it
        // re-registers on every non-terminal emit and releases on the first
        // terminal one, so the placeholder can never outlive the turn.
        placeholderTitle: t("chatsession.new_chat", { defaultValue: "New chat" }),
        // Cross-window sync (issue #2548) — same terminal-only contract as
        // the hydration wiring above.
        onSessionListSettled: () => publishSessionChange("renamed"),
        // Session-list delivery (issue #2513): same scope as this
        // function's own accept-time invalidate below.
        projectId,
        question,
        queryClient,
        sessionType: chatSessionType,
        snapshot: accepted.snapshot,
        // Honest signal, not a guess (issue #2513 finding 2) — currently
        // always `false` in practice since `accept` never attaches a
        // covering snapshot document, but wired to the real field rather
        // than hardcoded so a future backend change flows through.
        authoritative: Boolean(accepted.replayGap),
      })
      return "handled"
    } catch (error) {
      // No durable turn is running for this session, so nothing will ever
      // deliver the terminal release — the `finally` below drops the
      // placeholder for us (`placeholderHandedToAttach` is still `false` on
      // every throwing path, since none of them reach an attach). The legacy
      // retry below (and the legacy transport generally) titles the session
      // mid-stream and re-fetches an authoritative list afterwards.
      if (!chatRequestOwnsSession(chatStore, sessionIdForRequest, requestToken)) return "handled"
      if (shouldFallbackToLegacyTransport(error)) {
        // Fail-safe (issue #2513): the backend doesn't have durable turns
        // enabled for this mode even though the frontend's gate is open —
        // reachable in normal rollout (backend resolves enabled modes at
        // startup). Never surface this as a user-facing error; the legacy
        // transport is always available. Deliberately does NOT write any
        // request state here (no error, no isLoading/isAwaitingSave flip) —
        // the legacy path's own `initializeRequest` call (right after this
        // returns) writes the equivalent state for the same question, so
        // there is nothing to unwind and nothing to flash.
        logger.info(
          "Durable chat turn acceptance rejected as disabled — falling back to the legacy transport for this turn",
          { sessionId: sessionIdForRequest, turnId }
        )
        return "fallback-to-legacy"
      }
      // Fair use (#2625): the backend refuses a blocked user's turn BEFORE
      // acceptance, with `429 {error: "fair_use_blocked", usage: {...}}`.
      // Without this branch the raw `ApiError`'s technical message ("HTTP
      // error! status: 429\n...") would render as the answer's error card,
      // which tells the user nothing about why. Route the payload through the
      // same bridge the WS/durable paths use — it refreshes the usage
      // snapshot from the refusal (so the composer ring locks and the send
      // gate engages) and auto-opens the request-more popover — then show the
      // localized budget notice instead of an error, same shape as the 409
      // branch below.
      if (error instanceof ApiError && error.status === 429) {
        const fairUseCode = routeFairUseError(queryClient, error.body)
        if (fairUseCode) {
          logger.info("Durable chat turn refused by the fair-use gate", {
            sessionId: sessionIdForRequest,
            turnId,
          })
          writeRequestState({
            error: undefined,
            canContinue: true,
            isLoading: false,
            isStreaming: false,
            isAwaitingSave: false,
          })
          toast.error(t("fairUse.chat.blockedNotice"))
          return "handled"
        }
      }
      if (error instanceof ApiError && error.status === 409) {
        // R5 (durable-turn risk review): a 409 means the session's single
        // nonterminal slot is already pinned — either a genuinely live turn,
        // or a dead runner's lease that hasn't expired yet (1-5 minutes after
        // a crash — see the reconciler's recovery-lease window). 409 is
        // deliberately EXCLUDED from `shouldFallbackToLegacyTransport`
        // (falling back would start a second generator alongside a turn that
        // may still be alive — register item R2, fixed in f4366d95d — and
        // must not be reopened here). Without this branch the raw
        // `ApiError`'s technical message (`"HTTP error! status: 409\n..."`)
        // would render verbatim in the answer slot via `Chat.Error`. Same
        // "something is already in progress" message as the pre-flight
        // legacy-send guard below (`durable_turn_send_blocked`) — from the
        // user's perspective both are the identical situation, just caught
        // at different points (client-known-active vs server-discovered).
        logger.warn(
          "Durable chat turn acceptance conflicted (409) — an existing turn is still settling",
          {
            sessionId: sessionIdForRequest,
            turnId,
          }
        )
        writeRequestState({
          error: undefined,
          canContinue: true,
          isLoading: false,
          isStreaming: false,
          isAwaitingSave: false,
        })
        toast.error(
          t("questioninput.durable_turn_send_blocked", {
            defaultValue:
              "This chat already has a response in progress. Wait for it to finish before sending another message.",
          })
        )
        return "handled"
      }
      if (
        error instanceof ApiError &&
        error.status === 410 &&
        error.code === "conversation_forgotten"
      ) {
        // The user forgot this chat in Prism, which closes it for good: a
        // retry or continue can only repeat the refusal, so show the
        // start-new-chat notice instead of the error card.
        writeRequestState({
          error: undefined,
          canContinue: false,
          isLoading: false,
          isStreaming: false,
          isAwaitingSave: false,
        })
        toast.error(t("personalization.memoryPanel.prism.chatForgotten"))
        return "handled"
      }
      logger.error("Durable chat turn acceptance failed", error as Error)
      writeRequestState({
        error,
        canContinue: true,
        isLoading: false,
        isStreaming: false,
        isAwaitingSave: false,
      })
      return "handled"
    } finally {
      // L1 fix (durable activity-latency review round 2): the single release
      // point for the placeholder registered above. Every return in the `try`
      // and `catch` blocks above funnels through here — the only paths that
      // must NOT release are the ones that set `placeholderHandedToAttach`
      // because an attach (ours or another attacher's) now owns the
      // placeholder's lifecycle and will release it on the turn's first
      // terminal emit instead.
      if (!placeholderHandedToAttach) {
        releaseDurableSessionPlaceholder(sessionIdForRequest, turnId)
      }
      finishChatRequestControl(chatStore, sessionIdForRequest, requestToken)
      // Deliberately NO session-history invalidate here (issue #2513 finding
      // 2 — was previously unconditional except on the fallback path). This
      // used to fire right after HTTP accept, well before the backend has
      // titled the session, and the resulting refetch replaced the sidebar's
      // optimistic `{ title: "New chat" }` row with the server's still-
      // untitled (`title: ""`) one — which `use-chat-history.ts`'s
      // `sessions.filter((s) => s?.title)` then drops from the sidebar
      // entirely until the turn's very end. Nothing server-side changed at
      // accept that the optimistic insert (`ensureSession`/
      // `handleSendQuestion`) doesn't already reflect, and the durable path
      // delivers its own sidebar updates as the turn progresses —
      // `attachDurableChatTurnToSession`'s `deliverSessionEnrichment` (live,
      // as soon as a title exists) and its terminal invalidate (on
      // completion) — so there is nothing left for an accept-time refetch to
      // usefully do. This also removes the fallback-path's `await` window
      // that used to let a Stop click land between `finishChatRequestControl`
      // and control returning to `sendChatRequest` (see
      // use-chat.durable-turn-fallback.test.tsx) — now true for every path,
      // not just fallback, since there is no `await` left in this block at
      // all.
    }
  }

  /**
   * Returns `true` only when this send fully completed on the DURABLE
   * transport (issue #2513 finding 2) — never for legacy, a custom
   * transport, or a guard-clause bail-out. `handleSendQuestion` uses this to
   * skip its own post-send `invalidateQueries` for a durable turn: that
   * invalidate exists for LEGACY, where by the time `sendChatRequest`
   * resolves the stream has already finished and the session is titled —
   * firing it for a durable turn instead catches the send right after HTTP
   * accept, long before the backend has titled the session, and the
   * resulting refetch overwrites the sidebar's optimistic `"New chat"` row
   * with the server's still-untitled (`title: ""`) one, which the sidebar's
   * `sessions.filter((s) => s?.title)` then drops entirely until the turn
   * finishes. The durable path already delivers its own sidebar updates —
   * see `attachDurableChatTurnToSession`'s `deliverSessionEnrichment` and
   * terminal invalidate — so skipping this one is not a loss of coverage.
   */
  const sendChatRequest = async ({
    question,
    isContinuation = false,
    isRetry = false,
    videoId,
    selectedLanguages,
    startResearch = false,
    agentId,
    forceWebSearch,
    files: filesForRequest,
    userInputData,
  }: {
    question: string
    isContinuation?: boolean
    isRetry?: boolean
    videoId?: string
    selectedLanguages?: string[]
    startResearch?: boolean
    agentId?: string
    forceWebSearch?: boolean
    files?: File[]
    /** Text START-field values (key → string) to seed context.user_input_data. */
    userInputData?: Record<string, string>
  }): Promise<boolean> => {
    // Bind every transport, including custom transports, to a concrete origin
    // before it can perform work. Custom transport hooks retain their own
    // controller lifecycle, but cannot start from a null origin and later leak
    // state into whichever session becomes active.
    const sessionIdForRequest = getActiveSessionId()
    if (!sessionIdForRequest) {
      logger.error("Cannot send chat request without an active session")
      return false
    }

    if (getCustomChatResponse) {
      await getCustomChatResponse({ question, videoId, selectedLanguages })
      return false
    }

    // Durable turns (issue #2465): only ever reachable when the caller wires
    // BOTH `resolveDurableChatTurnMode` and `buildDurableChatTurnRequest` —
    // Translator/Video Insight/Dash pass neither, so this branch is dead code
    // for them, not merely flag-gated. Continuations are excluded: the
    // durable snapshot contract has no equivalent to the legacy
    // non-streaming-model `session_state` continuation path. Turns carrying
    // fresh (not-yet-indexed) uploads are excluded too: `buildDurableChatTurnRequest`
    // has no channel for base64 file bytes (the legacy WS reconstructs them from
    // `data.files`), so a durable send would silently drop them — keep those on
    // the legacy transport.
    //
    // `filesForRequest` is only ever non-empty for a caller that explicitly
    // passes `files` into `handleSendQuestion` (e.g. a retry). The ordinary
    // composer send does not — its pending attachments live only in the
    // store — so gating eligibility on `filesForRequest` alone missed them
    // entirely (issue #2513 finding 10): the send would still take the
    // durable path with `hasFreshUploads: false`, and only
    // `buildDurableChatTurnRequest`'s own `requestFiles ?? files` fallback
    // would recover the attachments — by then only to compute an effort
    // heuristic, never to actually include them, since the durable JSON
    // request still has no channel for the bytes. Resolving the SAME
    // fallback here, before eligibility is decided, is what makes an
    // ordinary attached-file send correctly stay on the legacy transport.
    const effectiveRequestFiles = filesForRequest ?? files
    const durableMode = resolveDurableChatTurnMode?.({ startResearch })
    const durableExplorerActive = isDurableExplorerSendEligible({
      isContinuation,
      hasFreshUploads: effectiveRequestFiles.length > 0,
      durableMode,
      isExplorerModeEnabled: isDurableChatTurnModeEnabled(config?.features, "explorer"),
      hasDurableRequestBuilder: buildDurableChatTurnRequest !== undefined,
    })
    if (durableExplorerActive) {
      const durableResult = await sendDurableExplorerChatTurn({
        forceWebSearch,
        isRetry,
        question,
        requestAgentId: agentId,
        requestFiles: effectiveRequestFiles,
        sessionIdForRequest,
        userInputData,
      })
      // "fallback-to-legacy" (issue #2513) falls through to the legacy
      // transport below instead of returning — the durable accept created no
      // server-side turn and registered no durable state (see
      // `sendDurableExplorerChatTurn`'s docstring), so retrying on legacy here
      // cannot double-send. Every other outcome is fully handled already.
      if (durableResult !== "fallback-to-legacy") return true
    }

    if (!getChatResponse) return false

    // R2(a) (durable chat turns risk review): serialize a legacy send
    // against an already-active durable turn on this session. The durable
    // branch above already guards itself against a second EXPLORER send
    // racing an existing durable turn (`sendDurableExplorerChatTurn`'s own
    // `existingDurableTurn` check), but a send that resolves to the LEGACY
    // transport (Deep Explorer or Research Agent toggled on —
    // `durableExplorerActive` false above) had no equivalent guard: a
    // second generator could start against the same session while the
    // durable turn's own UI mirror keeps repainting over it, and Stop would
    // mis-dispatch (see `handleCancelRequest`'s `hasLiveLegacyRequestAtCancel`
    // fall-through). Backend admission checking the durable ledger is the
    // real long-term fix and is explicitly out of scope here — this is a
    // client-side backstop that at minimum stops the FE from ever starting
    // the second stream itself, and tells the user why nothing happened
    // rather than silently dropping their message.
    const activeDurableTurnForLegacySend = readSessionState(sessionIdForRequest).durableTurn
    if (activeDurableTurnForLegacySend) {
      logger.warn("Legacy chat send blocked — a durable turn is already active for this session", {
        sessionId: sessionIdForRequest,
        turnId: activeDurableTurnForLegacySend.turnId,
      })
      toast.error(
        t("questioninput.durable_turn_send_blocked", {
          defaultValue:
            "This chat already has a response in progress. Wait for it to finish before sending another message.",
        })
      )
      return false
    }

    // Ownership lives outside the route hook so Stop and stale-write protection
    // survive Explorer unmount/remount. Starting a same-session request aborts
    // the previous transport after transferring ownership to the new token.
    const abortController = new AbortController()
    const { requestToken } = beginChatRequest(chatStore, sessionIdForRequest, abortController)
    const writeRequestState: RequestStateWriter = (partial) =>
      writeChatRequestState(
        { store: chatStore, sessionId: sessionIdForRequest, requestToken },
        partial
      )

    const initialResponse = initializeRequest({
      question,
      isContinuation,
      isRetry,
      requestFiles: filesForRequest,
      requestAgentId: agentId,
      writeRequestState,
    })

    // Track running workflow for sidebar indicator
    if (agentId) {
      useWorkflowRunStore.getState().setRunningWorkflowId(agentId, requestToken)
    }

    try {
      const response = await getChatResponse({
        ...(isRetry ? { isRetry: true } : {}),
        question,
        abortController,
        isContinuation,
        startResearch,
        agentId,
        forceWebSearch,
        files: filesForRequest,
        userInputData,
        requestToken,
      })
      if (!response.body) throw new Error("No response body")

      // TTFB measurement: backend connection established, response headers/first
      // bytes arriving. NOTE: this is time-to-first-*byte*, NOT time-to-first-token
      // — on reasoning models (gpt-5.x) the response stream opens here but the
      // first visible answer token can arrive much later, after reasoning. Don't
      // read this as "the answer started" (see #2098).
      performance.mark("chat_first_response")
      try {
        const measure = performance.measure("chat_ttfb", "chat_send", "chat_first_response")
        logger.info(`TTFB: ${Math.round(measure.duration)}ms (send → response headers)`)
      } catch {
        /* marks may not exist */
      }

      // Check if using a reasoning model - if so, don't stream
      const shouldStreamResponse =
        shouldStream && (gptModel?.feature_flags?.supports_streaming ?? true)

      if (shouldStreamResponse) {
        await handleStreamRequest({
          question,
          responseBody: response.body,
          signal: abortController.signal,
          isContinuation,
          writeChatState: writeRequestState,
          initialResponse: initialResponse,
          sessionId: sessionIdForRequest,
          getChatState: () => {
            // Always read from the live store
            const storeState = chatStore.getState()
            if (!(sessionIdForRequest in storeState.sessionStates)) {
              return { messages: [], streamingAnswer: null }
            }
            const sessionState = storeState.sessionStates[sessionIdForRequest]
            return {
              messages: sessionState.messages,
              streamingAnswer: sessionState.streamingAnswer,
            }
          },
        })
      } else {
        await handleNonStreamingResponse({
          response,
          question,
          isContinuation,
          sessionId: sessionIdForRequest,
          writeRequestState,
        })
      }

      notifyBackgroundChatCompletion({
        requestToken,
        sessionId: sessionIdForRequest,
      })

      // Backend handles message saving via message_saved event - no need to check session
    } catch (error) {
      handleRequestError(error, { sessionId: sessionIdForRequest, requestToken })
    } finally {
      // The turn is terminally done for this request — retire its steer state
      // (chips, steer_ready, cancel bookkeeping). pendingRedirect survives: the
      // stop-and-redirect effect consumes it once message_saved lands too. The
      // owner token prevents an older request from clearing a newer turn's state.
      clearSteerTurnState(sessionIdForRequest, requestToken)
      finishChatRequestControl(chatStore, sessionIdForRequest, requestToken)
      // Clear workflow running state
      if (agentId) {
        useWorkflowRunStore.getState().clearRunningWorkflowId(requestToken)
      }
      await queryClient.invalidateQueries({
        queryKey: queryKeys.sessionHistory(chatSessionType, projectId),
      })
    }
    return false
  }

  /**
   * Speculatively create a session if one doesn't exist yet.
   * Call on first keystroke / focus to move session creation off the critical path.
   * Safe to call multiple times — no-op if a session already exists.
   *
   * Uses an in-flight ref to dedupe concurrent calls. Without this, rapid
   * successive calls (e.g. the chatbox's isFirstKeystroke firing repeatedly
   * when React's controlled `question` state resets between keystrokes)
   * would each pass the getOpenSession guard before any of them finished,
   * spawning multiple empty chat sessions.
   */
  const ensureSessionInFlightRef = useRef<Promise<void> | null>(null)
  const ensureSession = async (): Promise<void> => {
    if (chatService.getOpenSession(chatSessionType)) return
    if (ensureSessionInFlightRef.current) {
      return ensureSessionInFlightRef.current
    }

    const sessionHistoryKey = queryKeys.sessionHistory(chatSessionType, projectId)
    const newSessionTitle = t("chatsession.new_chat", { defaultValue: "New chat" })

    const run = async (): Promise<void> => {
      try {
        const createdSession = await chatService.createSession(
          chatSessionType,
          "",
          undefined,
          false,
          undefined,
          projectId
        )

        if (!createdSession?.id) return

        const normalizedTitle = createdSession.title?.trim()
          ? createdSession.title
          : newSessionTitle
        const sessionWithTitle: NonNullable<ChatSessionModel> = {
          ...createdSession,
          id: createdSession.id,
          title: normalizedTitle,
        }

        chatService.openSession(chatSessionType, sessionWithTitle)
        setCurrentSessionId(sessionWithTitle.id ?? null)

        await queryClient.cancelQueries({ queryKey: sessionHistoryKey })
        queryClient.setQueryData(
          sessionHistoryKey,
          (oldSessions: ChatSessionModel[] | undefined) => {
            const sessions = oldSessions || []
            const remainingSessions = sessions.filter(
              (existingSession) => existingSession?.id !== sessionWithTitle.id
            )
            return [sessionWithTitle, ...remainingSessions]
          }
        )

        // Update the URL silently via the History API instead of React Router's
        // navigate(). The session is already opened in chatService and tracked
        // in state above; calling navigate() here would re-trigger useSessionSync
        // and cause a visible re-render ("blink") while the user is typing.
        // Silent URL update keeps refresh/back/share working without remounting.
        if (typeof window !== "undefined") {
          const newUrl = `${getRouteChatPath(moduleRoute.id)}/${sessionWithTitle.id}`
          window.history.replaceState(window.history.state, "", newUrl)
        }
        // Hint the browser's other windows that this list changed (issue
        // #2548). They refetch the authoritative list — they are never told
        // the row, so a session that is not yet listable server-side simply
        // does not appear there, exactly as it would on a manual reload.
        publishSessionChange("created")
        logger.info("Optimistic session created", { sessionId: sessionWithTitle.id })
      } catch (error) {
        // Non-fatal — handleSendQuestion will retry session creation if needed
        logger.warn("Optimistic session creation failed (will retry on send)", error as Error)
      }
    }

    const promise = run().finally(() => {
      ensureSessionInFlightRef.current = null
    })
    ensureSessionInFlightRef.current = promise
    return promise
  }

  const handleExampleClicked = async (example: string) => {
    await handleSendQuestion({ question: example })
  }

  const handleSendQuestion = async ({
    question,
    isRetry,
    videoId,
    selectedLanguages,
    startResearch,
    agentId,
    forceWebSearch,
    files: filesForRequest,
    user_input_data: userInputData,
  }: {
    question: string
    isRetry?: boolean
    videoId?: string
    selectedLanguages?: string[]
    startResearch?: boolean
    agentId?: string
    forceWebSearch?: boolean
    files?: File[]
    /**
     * Text START-field values (key → string) for a workflow run. Forwarded to
     * the backend so context.user_input_data is seeded even when files are
     * present (files ride the upload pipeline; this carries only text fields).
     */
    user_input_data?: Record<string, string>
  }) => {
    // TTFT measurement: mark the moment the user hits send
    try {
      performance.clearMarks("chat_send")
      performance.clearMarks("chat_first_response")
    } catch {
      /* no prior marks */
    }
    performance.mark("chat_send")

    let session = chatService.getOpenSession(chatSessionType)

    const sessionHistoryKey = queryKeys.sessionHistory(chatSessionType, projectId)

    let didNavigate = false

    try {
      // A first keystroke may already be creating this session. Adopt that
      // request instead of starting another createSession call: competing
      // creates can route the stream and session sync through different IDs,
      // leaving duplicate optimistic messages until the next hydration.
      if (!session && ensureSessionInFlightRef.current) {
        await ensureSessionInFlightRef.current
        session = chatService.getOpenSession(chatSessionType)
        if (session?.id) {
          // ensureSession uses replaceState to avoid a typing-time remount, but
          // React Router does not observe that History API write. Once Send
          // adopts the created session, synchronize the route just as the
          // create-on-send fallback does so useSessionSync keeps this session
          // active while the first request is in flight.
          void navigate(`${getRouteChatPath(moduleRoute.id)}/${session.id}`, { replace: true })
          didNavigate = true
        }
      }

      // Create a session only when pre-creation has not run (or did not yield
      // one). This remains the fallback for immediate sends.
      if (!session) {
        const newSessionTitle = t("chatsession.new_chat", { defaultValue: "New chat" })
        const createdSession = await chatService.createSession(
          chatSessionType,
          "",
          undefined,
          false,
          undefined,
          projectId
        )

        if (!createdSession?.id) {
          throw new Error("Failed to create chat session")
        }

        const normalizedTitle = createdSession.title?.trim()
          ? createdSession.title
          : newSessionTitle
        const sessionWithTitle: NonNullable<ChatSessionModel> = {
          ...createdSession,
          id: createdSession.id,
          title: normalizedTitle,
        }

        chatService.openSession(chatSessionType, sessionWithTitle)
        setCurrentSessionId(sessionWithTitle.id ?? null)

        await queryClient.cancelQueries({ queryKey: sessionHistoryKey })

        queryClient.setQueryData(
          sessionHistoryKey,
          (oldSessions: ChatSessionModel[] | undefined) => {
            const sessions = oldSessions || []
            const remainingSessions = sessions.filter(
              (existingSession) => existingSession?.id !== sessionWithTitle.id
            )
            return [sessionWithTitle, ...remainingSessions]
          }
        )

        // Same hint as `ensureSession`'s (issue #2548) — this is the branch
        // taken when the speculative creation never ran or failed.
        publishSessionChange("created")

        session = sessionWithTitle
        void navigate(`${getRouteChatPath(moduleRoute.id)}/${session.id}`)
        didNavigate = true
      }

      const tookDurablePath = await sendChatRequest({
        question,
        isRetry,
        videoId,
        selectedLanguages,
        startResearch,
        agentId,
        forceWebSearch,
        files: filesForRequest,
        userInputData,
      })

      // Skip this refetch for a durable turn (issue #2513 finding 2): unlike
      // legacy — where `sendChatRequest` doesn't resolve until the stream
      // has fully finished and the session is titled — the durable path
      // resolves right after HTTP accept, so this invalidate would ALSO
      // catch the session before it has a title and drop it from the
      // sidebar (`sessions.filter((s) => s?.title)`) for the rest of the
      // turn. See `sendChatRequest`'s own docstring for the full chain and
      // why the durable path's own sidebar delivery
      // (`attachDurableChatTurnToSession`) makes this redundant there
      // anyway, even outside the bug.
      if (!tookDurablePath) {
        await queryClient.invalidateQueries({
          queryKey: sessionHistoryKey,
        })
      }

      // Only navigate if:
      // 1. We just created this session (didNavigate = true already handled above), OR
      // 2. This session is active AND user is still in this module
      // NEVER navigate to a background session or across modules.
      // Skip when already on the session URL — a same-path navigate() re-triggers
      // session sync and feels like a remount (painful when the message queue
      // auto-sends the next turn immediately after settle).
      const isStillInThisModule = path.pathname.includes(`/${moduleRoute.id}/`)
      const targetPath = session?.id ? `${getRouteChatPath(moduleRoute.id)}/${session.id}` : null
      if (
        !didNavigate &&
        session?.id &&
        targetPath &&
        path.pathname !== targetPath &&
        session.id === getActiveSessionId() &&
        isStillInThisModule
      ) {
        void navigate(targetPath)
      }
    } catch (error) {
      logger.error("Error in handleSendQuestion:", error as Error)

      changeChatState({
        error,
        isLoading: false,
        isStreaming: false,
        isAwaitingSave: false,
      })
    }
  }

  /** Retire a session's steer turn state (chips, steer_ready, cancel
   *  bookkeeping) once its request is terminally done. Session-scoped fresh
   *  read — the user may have switched sessions since the request started.
   *  No-ops (no store write) when everything is already default, so flag-off
   *  turns are untouched.
   *
   *  Issue #2469: delivered chips are dropped (feed/activity confirm); missed
   *  pending notes requeue when explorer_message_queue is on. */
  const applySteerTurnEndSettlement = (sessionState: SessionState): Partial<SessionState> => {
    const settlement = settleSteerNotesAtTurnEnd(sessionState.steerNotes, isMessageQueueEnabled)
    return {
      steerNotes: settlement.steerNotes,
      ...(settlement.queuedMessagesToAppend.length > 0
        ? {
            queuedMessages: [...sessionState.queuedMessages, ...settlement.queuedMessagesToAppend],
          }
        : {}),
    }
  }

  const clearSteerTurnState = (sessionId: string | null, requestToken?: symbol) => {
    if (!sessionId) return
    if (requestToken && !chatRequestOwnsSession(chatStore, sessionId, requestToken)) return
    clearSteerCancelWatchdog(sessionId)
    const store = chatStore.getState()
    const sessionState = store.sessionStates[sessionId]
    if (!sessionState) return
    const hasSteerState =
      sessionState.steerReady ||
      sessionState.cancelPending ||
      sessionState.cancelAckReceived ||
      sessionState.steerNotes.length > 0
    if (!hasSteerState) return
    store.updateSession(sessionId, {
      steerReady: false,
      ...applySteerTurnEndSettlement(sessionState),
      cancelPending: false,
      cancelAckReceived: false,
    })
  }

  /** Watchdog fallback: the server did not wind the turn down in time (no
   *  cancel_ack within 5s, or no terminal end within the save budget after the
   *  ack) — never strand the user, fall back to the legacy hard abort. */
  const performSteerFallbackAbort = (sessionId: string, requestControl: ChatRequestControl) => {
    const store = chatStore.getState()
    const sessionState = store.sessionStates[sessionId]
    // cancelPending is cleared when the turn ends normally — nothing to do.
    if (!sessionState?.cancelPending) return
    if (!chatRequestOwnsSession(chatStore, sessionId, requestControl.requestToken)) return
    logger.warn("Steer cancel watchdog expired — falling back to legacy abort", { sessionId })
    if (sessionState.pendingRedirect) {
      restoreSteerComposerText(sessionState.pendingRedirect.text)
      toast.error(
        t("questioninput.steer_redirect_dropped", {
          defaultValue: "The response didn't stop in time — your queued message was not sent.",
        })
      )
    }
    if (
      !cancelOwnedRequest({
        sessionId,
        requestToken: requestControl.requestToken,
        requestControl,
        state: sessionState,
      })
    ) {
      return
    }
    store.updateSession(sessionId, {
      steerReady: false,
      ...applySteerTurnEndSettlement(sessionState),
      cancelPending: false,
      cancelAckReceived: false,
      pendingRedirect: null,
    })
  }

  const armSteerCancelWatchdog = (sessionId: string, requestControl: ChatRequestControl) => {
    clearSteerCancelWatchdog(sessionId)
    const timers = steerCancelTimersRef.current
    timers.set(
      sessionId,
      setTimeout(() => {
        timers.delete(sessionId)
        const sessionState = chatStore.getState().sessionStates[sessionId]
        if (!sessionState?.cancelPending) return
        if (!sessionState.cancelAckReceived) {
          performSteerFallbackAbort(sessionId, requestControl)
          return
        }
        // Acked: the server owns the wind-down now (partial save can take up to
        // its 60s budget). Only abort if even that window passes with the turn
        // still open.
        timers.set(
          sessionId,
          setTimeout(() => {
            timers.delete(sessionId)
            performSteerFallbackAbort(sessionId, requestControl)
          }, STEER_CANCEL_WINDDOWN_TIMEOUT_MS)
        )
      }, STEER_CANCEL_ACK_TIMEOUT_MS)
    )
  }

  /** Steer stage-2 stop: send a cancel frame (server winds down and persists
   *  the partial), stash any composer draft for stop-and-redirect, and arm the
   *  ack watchdog. Returns false when the frame could not be sent — the caller
   *  falls back to the legacy abort.
   *
   *  `overrideRedirectText`, when given, is used as the redirect text as-is
   *  (trimmed) instead of the live composer draft — the composer's peek/clear
   *  is skipped entirely, since the text in that case belongs to a queued
   *  bubble ("Force now"), not to whatever the user is currently typing. */
  const requestSteerCancel = (
    sessionId: string,
    requestControl: ChatRequestControl,
    overrideRedirectText?: string
  ): boolean => {
    if (!chatRequestOwnsSession(chatStore, sessionId, requestControl.requestToken)) return false
    try {
      sendSteerControlFrame(sessionId, { type: "cancel" })
    } catch (error) {
      logger.error("Steer cancel frame send failed — falling back to abort", error as Error)
      toast.error(
        t("questioninput.steer_cancel_failed", {
          defaultValue: "Couldn't reach the server — stopping the response locally.",
        })
      )
      return false
    }
    // Stash the composer draft only after the cancel frame went out; on the
    // send-failure path above the draft stays untouched in the composer.
    // Skip the composer entirely when an override is given (Force now).
    const redirectText =
      overrideRedirectText !== undefined
        ? overrideRedirectText.trim()
        : peekSteerComposerText().trim()
    if (overrideRedirectText === undefined && redirectText) {
      clearSteerComposerText()
    }
    if (
      !writeChatRequestState(
        { store: chatStore, sessionId, requestToken: requestControl.requestToken },
        {
          cancelPending: true,
          cancelAckReceived: false,
          ...(redirectText ? { pendingRedirect: { text: redirectText, armedAt: Date.now() } } : {}),
        }
      )
    ) {
      return false
    }
    armSteerCancelWatchdog(sessionId, requestControl)
    return true
  }

  /** Force now against a DURABLE turn (issue #2813).
   *
   *  Same user-visible gesture as the legacy path — stop this turn, keep its
   *  partial, run `text` next — but assembled from the durable primitives:
   *  `cancelDurableChatTurnApi` sets `cancelRequestedAt`, and the runner
   *  already persists whatever it streamed for a stopped turn. No steer
   *  channel and no `cancel_ack` are involved.
   *
   *  The redirect is stashed synchronously (before the await) so a Force can
   *  never look like it dropped the queued text, and is tagged
   *  `viaDurableTurn` so the auto-send effect waits on the turn settling
   *  rather than on a `message_saved` ack that never comes.
   *
   *  Cancel authority is the backend's (`can_send_message`), exactly as in
   *  `handleCancelRequest`'s durable branch — no client-side ownership guess.
   *
   *  A 409 `turn_terminal` means the turn already settled on the server; keep
   *  the redirect armed and only clear `cancelPending` so auto-send can fire
   *  once the local mirror drops `durableTurn`. Un-arm / restore the composer
   *  only on 403 (this caller cannot stop the turn) or other cancel failures
   *  while the captured identity is still bound. */
  const requestDurableForceRedirect = (sessionId: string, text: string): boolean => {
    const capturedTurn = chatState.durableTurn
    if (!capturedTurn) return false
    const redirectText = text.trim()
    if (!redirectText) return false

    writeSessionState(sessionId, {
      cancelPending: true,
      pendingRedirect: { text: redirectText, armedAt: Date.now(), viaDurableTurn: true },
    })

    void (async () => {
      try {
        await cancelDurableChatTurnApi(
          capturedTurn.sessionId,
          { turnId: capturedTurn.turnId, reason: "user_stop" },
          settingsService.token
        )
      } catch (error) {
        const isForbidden = error instanceof ApiError && error.status === 403
        const isTurnAlreadyTerminal =
          error instanceof ApiError && error.status === 409 && error.code === "turn_terminal"

        if (isForbidden) {
          toast.error(
            t("questioninput.durable_turn_not_owned", {
              defaultValue: "This response was started elsewhere and can't be stopped from here.",
            })
          )
        } else if (!isTurnAlreadyTerminal) {
          logger.error("Durable force-redirect cancel failed", error as Error)
        }

        const latest = readSessionState(sessionId)
        const capturedIdentityStillBound =
          latest.durableTurn?.turnId === capturedTurn.turnId &&
          latest.durableTurn.epoch === capturedTurn.epoch
        // Auto-send may already be in flight if the local mirror dropped this
        // turn — never clobber pendingRedirect / cancelPending then.
        if (!capturedIdentityStillBound) return

        if (isTurnAlreadyTerminal) {
          // Cancel lost the race with server-side settle. Keep the redirect
          // armed so auto-send can fire once durableTurn clears.
          writeSessionState(sessionId, { cancelPending: false })
          return
        }

        // 403 (not-owned) or other cancel failures (network / 5xx): the turn
        // is still running, so the redirect must not stay armed waiting for a
        // settle that is not coming.
        writeSessionState(sessionId, { cancelPending: false, pendingRedirect: null })
        restoreSteerComposerText(redirectText)
      }
    })()

    return true
  }

  /** "Force now" (issue #2438 scope-widening): stop the current turn and run
   *  a specific QUEUED message next, reusing the stop-and-redirect path above
   *  with the bubble's text as an explicit override. Mirrors the
   *  `steerStopActive` gate in handleCancelRequest — only armable while the
   *  server has announced steer_ready for the running turn and no cancel is
   *  already in flight. */
  const requestForceRedirect = (text: string): boolean => {
    const sessionId = getActiveSessionId()
    if (!sessionId) return false
    // Durable turns (issue #2813) never announce `steer_ready` — they run off
    // the HTTP surface, which has no WS control channel to carry it — so the
    // legacy gate below can never arm for them. Their stop-and-redirect is
    // built from parts durable already has (cancel + the runner's partial
    // persistence), so it gets its own branch BEFORE the legacy path rather
    // than trying to force the WS-shaped one to fit.
    if (chatState.durableTurn && steerStopRedirectEnabled && !chatState.cancelPending) {
      return requestDurableForceRedirect(sessionId, text)
    }
    const requestControl = getChatRequestControl(chatStore, sessionId)
    if (!requestControl) return false
    const steerStopActive =
      steerStopRedirectEnabled &&
      chatState.steerReady &&
      !chatState.cancelPending &&
      (chatState.isLoading || chatState.isStreaming)
    if (!steerStopActive) return false
    const succeeded = requestSteerCancel(sessionId, requestControl, text)
    // Force lands as a normal full-turn user bubble (same as a typed send).
    // Do not attach a compact "Forced:" provenance pill to the primary message
    // — that pill style is reserved for steered marker bubbles only.
    return succeeded
  }

  // Stop-and-redirect (issue #2438): auto-send the stashed draft only after
  // BOTH terminal conditions hold — the cancelled turn's stream is done
  // (its finally cleared cancelPending) AND the partial is persisted by a real
  // `message_saved` ack (sawMessageSaved; the save-guard watchdog also clears
  // isAwaitingSave, but that means persistence failed and the redirect must
  // not fire on top of an unsaved turn). The send goes through the composer's
  // registered send entry — the same path a user click takes (token-limit
  // gate, prefix handling) — and pendingRedirect is cleared only after that
  // send resolves, so a failure restores the draft instead of losing it.
  const redirectInFlightBySessionRef = useRef(
    new Map<
      string,
      {
        disposalGeneration: number
        operation: NonNullable<SessionState["pendingRedirect"]>
      }
    >()
  )
  useEffect(() => {
    const pendingRedirect = chatState.pendingRedirect
    if (!pendingRedirect) return
    if (chatState.cancelPending || chatState.isLoading || chatState.isStreaming) return
    if (chatState.isAwaitingSave) return
    const sessionId = getActiveSessionId()
    if (!sessionId) return
    const disposalGeneration = getChatRequestSessionDisposalGeneration(chatStore, sessionId)
    const currentOwner = redirectInFlightBySessionRef.current.get(sessionId)
    if (currentOwner?.disposalGeneration === disposalGeneration) return
    const dropRedirect = () => {
      const state = chatStore.getState()
      if (isChatRequestSessionDisposed(chatStore, sessionId) || !state.sessionStates[sessionId]) {
        return
      }
      state.updateSession(sessionId, { pendingRedirect: null })
    }

    // A stale redirect (session revisited long after the cancel) must not
    // auto-fire — put the text back in the composer instead.
    if (Date.now() - pendingRedirect.armedAt > STEER_REDIRECT_MAX_AGE_MS) {
      dropRedirect()
      restoreSteerComposerText(pendingRedirect.text)
      return
    }
    // Durable turns (issue #2813) never emit `message_saved` — the runner owns
    // persistence and `_translate_stream_frame` drops that legacy frame — so
    // `sawMessageSaved` would be permanently false and every durable Force
    // would land here and be dropped. Their equivalent "terminal AND persisted"
    // signal is the registry clearing `durableTurn`, which the mirror only does
    // once the canonical terminal status arrives, and the runner only settles
    // terminal after persisting the partial. While it is still set the turn has
    // not settled yet, so wait rather than dropping.
    if (pendingRedirect.viaDurableTurn) {
      if (chatState.durableTurn) return
    } else if (!chatState.sawMessageSaved) {
      dropRedirect()
      restoreSteerComposerText(pendingRedirect.text)
      toast.error(
        t("questioninput.steer_redirect_dropped", {
          defaultValue: "The response didn't stop in time — your queued message was not sent.",
        })
      )
      return
    }
    const composerSend = getSteerComposerSend()
    const completionStillTargetsOrigin = () =>
      getActiveSessionId() === sessionId &&
      isChatRequestSessionDisposalGenerationCurrent(chatStore, sessionId, disposalGeneration) &&
      chatStore.getState().sessionStates[sessionId]?.pendingRedirect === pendingRedirect
    const ownership = { disposalGeneration, operation: pendingRedirect }
    redirectInFlightBySessionRef.current.set(sessionId, ownership)
    void (async () => {
      try {
        // Prefer the composer's canonical send; fall back to the direct
        // sender only when no composer is mounted (then there is no composer
        // to restore the draft into either).
        const result = composerSend
          ? await composerSend(pendingRedirect.text)
          : await handleSendQuestion({ question: pendingRedirect.text })
        if (!completionStillTargetsOrigin()) return
        if (result === false) {
          dropRedirect()
          restoreSteerComposerText(pendingRedirect.text)
          toast.error(
            t("questioninput.steer_redirect_blocked", {
              defaultValue:
                "Your queued message couldn't be sent automatically — it's back in the composer.",
            })
          )
          return
        }
        dropRedirect()
      } catch (error) {
        logger.error("Stop-and-redirect send failed — restoring draft", error as Error)
        if (!completionStillTargetsOrigin()) return
        dropRedirect()
        restoreSteerComposerText(pendingRedirect.text)
        toast.error(
          t("questioninput.steer_redirect_blocked", {
            defaultValue:
              "Your queued message couldn't be sent automatically — it's back in the composer.",
          })
        )
      } finally {
        if (redirectInFlightBySessionRef.current.get(sessionId) === ownership) {
          redirectInFlightBySessionRef.current.delete(sessionId)
          const state = chatStore.getState()
          const successor = state.sessionStates[sessionId]?.pendingRedirect
          if (
            successor &&
            successor !== pendingRedirect &&
            getActiveSessionId() === sessionId &&
            isChatRequestSessionDisposalGenerationCurrent(chatStore, sessionId, disposalGeneration)
          ) {
            // Releasing an owner is ref-only and does not render. Clone the
            // waiting operation so this effect gets one explicit retry.
            state.updateSession(sessionId, { pendingRedirect: { ...successor } })
          }
        }
      }
    })()
  }, [
    chatState.pendingRedirect,
    chatState.cancelPending,
    chatState.isLoading,
    chatState.isStreaming,
    chatState.isAwaitingSave,
    chatState.sawMessageSaved,
    // A durable redirect waits on this clearing (issue #2813); without it the
    // effect never re-runs when the turn settles and the redirect never fires.
    chatState.durableTurn,
  ])

  const getLocallyStoppedState = (state: SessionState): Partial<SessionState> => {
    const completedStreamingAnswer: [string, ChatAppResponse] | null = state.streamingAnswer
      ? [state.streamingAnswer[0], { ...state.streamingAnswer[1], completed: true }]
      : null

    return {
      error: undefined,
      isLoading: false,
      isStreaming: false,
      isAwaitingSave: false,
      canContinue: true,
      ...(state.streamingAnswer ? { streamingAnswer: completedStreamingAnswer } : {}),
    }
  }

  const cancelOwnedRequest = ({
    sessionId,
    requestToken,
    requestControl,
    state,
  }: {
    sessionId: string
    requestToken: symbol
    requestControl?: ChatRequestControl
    state: SessionState
  }) => {
    if (!chatRequestOwnsSession(chatStore, sessionId, requestToken)) return false

    writeChatRequestState(
      { store: chatStore, sessionId, requestToken },
      getLocallyStoppedState(state)
    )
    setChatRequestAborted({ store: chatStore, sessionId, requestToken }, [
      state.lastQuestion[0] || state.streamingAnswer?.[0] || "",
      undefined,
    ])

    // Invalidate ownership before dispatching abort so stream-handler error and
    // pending debounced writes cannot repaint content captured before Stop.
    retireChatRequest(chatStore, sessionId, requestToken)
    requestControl?.abortController.abort()
    return true
  }

  const settleSteerAfterHardAbort = (sessionId: string, state: SessionState) => {
    clearSteerCancelWatchdog(sessionId)
    if (state.pendingRedirect) {
      restoreSteerComposerText(state.pendingRedirect.text)
      toast.error(
        t("questioninput.steer_redirect_dropped", {
          defaultValue: "The response didn't stop in time — your queued message was not sent.",
        })
      )
    }
    chatStore.getState().updateSession(sessionId, {
      steerReady: false,
      ...applySteerTurnEndSettlement(state),
      cancelPending: false,
      cancelAckReceived: false,
      pendingRedirect: null,
    })
  }

  const handleCancelRequest = async () => {
    const sessionIdForCancel = getActiveSessionId()
    if (!sessionIdForCancel) return

    const stateAtCancel = readSessionState(sessionIdForCancel)

    // Adversarial-review follow-up (R2(b)): once a durable turn is attached
    // to this session, `attachDurableChatTurnToSession`'s `writePatch`
    // unconditionally overwrites the shared `streamingAnswer` field on every
    // registry emit — unlike the legacy WS/NDJSON path, there is no
    // per-request ownership tag on it (see `stream-turn-ownership.ts`'s
    // `streamOwnsStreamingSlot`, which guards the analogous same-transport
    // case). So `stateAtCancel.streamingAnswer` captured above can already be
    // the DURABLE turn's own in-progress (or just-preserved-stale, see
    // `clearStaleDurableIdentity`) content — `buildChatAppResponseFromSnapshot`
    // always stamps it `durable_turn: true` (durable-chat-turn-state.ts) —
    // rather than the legacy request's, whenever both exist for this session
    // at once (the fall-through below) OR a durable answer was left behind by
    // `clearStaleDurableIdentity` after `durableTurn` itself already cleared.
    // Falling through into the legacy-cancel branches with that content would
    // mark it `completed: true` via `getLocallyStoppedState` and write it back
    // as if it were the legacy answer — resurrecting/duplicating the durable
    // turn's own text. Strip it before it can reach `cancelOwnedRequest` /
    // `getLocallyStoppedState`, the only two consumers of `state.streamingAnswer`
    // in the legacy-cancel paths below; used in place of `stateAtCancel`
    // everywhere from here down.
    const legacyStateAtCancel: SessionState = stateAtCancel.streamingAnswer?.[1].durable_turn
      ? { ...stateAtCancel, streamingAnswer: null }
      : stateAtCancel

    // Read every legacy-transport signal up front — R2(b) (durable chat
    // turns risk review) needs these regardless of whether a durable turn is
    // ALSO active on this session (see the fall-through below), so they can
    // no longer be captured only after the durable branch's own early
    // return.
    const queueRequestIdAtCancel = stateAtCancel.queueRequestId
    const requestControlAtCancel = getChatRequestControl(chatStore, sessionIdForCancel)
    const ownerTokenAtCancel = getChatRequestOwnerToken(chatStore, sessionIdForCancel)
    const ownedRequestControl =
      ownerTokenAtCancel && requestControlAtCancel?.requestToken === ownerTokenAtCancel
        ? requestControlAtCancel
        : undefined
    // Whether this session also has a live LEGACY request outstanding right
    // now, independent of the durable turn below. The two transports can't
    // normally coexist post-send (the legacy send path now bails out while a
    // durable turn is active — see `sendChatRequest`'s own
    // `activeDurableTurnForLegacySend` guard), but a legacy request already
    // in flight BEFORE the durable turn attached (e.g. this tab's own
    // hydration poll discovering another window's turn mid-request) is a
    // real, reachable race, and Stop must release BOTH — not just whichever
    // one this function happens to check first.
    //
    // Deliberately `ownedRequestControl`, NOT `ownerTokenAtCancel`:
    // `finishChatRequestControl` clears `control` but *retains* `ownerToken`
    // once the legacy transport finishes, purely so a delayed settle
    // callback can still write session state (see chat-request-registry.ts).
    // An ordinary durable turn also runs `beginChatRequest` /
    // `finishChatRequestControl` for its own bookkeeping token, so
    // `ownerTokenAtCancel` is truthy on essentially every durable turn even
    // though there is no live legacy transport left to stop. Gating on the
    // retained token here would make Stop fall through into legacy teardown
    // (`cancelOwnedRequest` / `settleSteerAfterHardAbort`) for a turn that
    // has nothing legacy running. `ownedRequestControl` is only set while an
    // actual stoppable `AbortController` is registered, so it — not the
    // token — is the true liveness signal.
    const hasLiveLegacyRequestAtCancel = Boolean(queueRequestIdAtCancel || ownedRequestControl)

    // Exact Stop for a durable turn (issue #2465): capture sessionId + epoch +
    // turnId at click time — synchronously, before any await — and post
    // cancellation for exactly that turn. Never touches the legacy
    // abortController/steer path (those only exist for the legacy transport's
    // own `beginChatRequest` bookkeeping token, which durable turns also hold
    // but whose `.signal` is never wired to anything). The subscription stays
    // open: `attachDurableChatTurnToSession`'s mirror clears `durableTurn`
    // and settles UI state only once the canonical `cancelled` status
    // actually arrives — never optimistically here.
    if (stateAtCancel.durableTurn) {
      const capturedTurn = stateAtCancel.durableTurn
      // No FE ownership gate (issue #2513): `durableTurn` also gets populated
      // by hydration for a tab that never sent this turn (a second tab,
      // another device, this same tab reopened after a close) — but the
      // backend is the actual authority on who may stop a turn: cancel is
      // gated on `can_send_message` (session write access), same as any
      // other send, not on "did this tab's JS send it" (see
      // `has_session_access` vs `can_send_message` in
      // app/backend/routes/chat_turns.py). Attempt the cancel unconditionally
      // and let a 403 be the authoritative "you may not stop this" signal —
      // never block a legitimate, backend-authorized Stop on a client-side
      // guess that buys no real security.
      writeSessionState(sessionIdForCancel, { cancelPending: true })
      try {
        await cancelDurableChatTurnApi(
          capturedTurn.sessionId,
          { turnId: capturedTurn.turnId, reason: "user_stop" },
          settingsService.token
        )
      } catch (error) {
        if (error instanceof ApiError && error.status === 403) {
          toast.error(
            t("questioninput.durable_turn_not_owned", {
              defaultValue: "This response was started elsewhere and can't be stopped from here.",
            })
          )
        } else {
          logger.error("Durable chat turn cancel request failed", error as Error)
        }
        // Only clear the local "cancelling" indicator if this exact turn is
        // still bound to the session — a stale failure must never touch a
        // newer turn's state.
        const latest = readSessionState(sessionIdForCancel)
        if (
          latest.durableTurn?.turnId === capturedTurn.turnId &&
          latest.durableTurn.epoch === capturedTurn.epoch
        ) {
          writeSessionState(sessionIdForCancel, { cancelPending: false })
        }
      }
      // R2(b): a durable turn is never the ONLY thing that can be running
      // for this session (see `hasLiveLegacyRequestAtCancel` above) — fall
      // through to also stop the legacy request captured before this block
      // instead of returning here unconditionally. Stop must stop
      // everything actually running for the session, not just whichever
      // transport this branch happens to own.
      if (!hasLiveLegacyRequestAtCancel) return
    }

    if (queueRequestIdAtCancel) {
      // A stale queue id may coexist with a newer live stream. Stop owns the
      // exact turn captured at click time, so retire it before crossing the API
      // await boundary; a later request then cannot be mistaken for this one.
      const cancelledOwner = ownerTokenAtCancel
        ? cancelOwnedRequest({
            sessionId: sessionIdForCancel,
            requestToken: ownerTokenAtCancel,
            requestControl: ownedRequestControl,
            state: legacyStateAtCancel,
          })
        : false
      if (cancelledOwner) {
        settleSteerAfterHardAbort(sessionIdForCancel, legacyStateAtCancel)
      } else if (!ownerTokenAtCancel) {
        // A queued turn can remain awaiting its backend acknowledgement after
        // transport ownership retired. Release only its visible UI state.
        writeSessionState(sessionIdForCancel, getLocallyStoppedState(legacyStateAtCancel))
      }

      try {
        await cancelQueueRequestApi(queueRequestIdAtCancel, settingsService.token)
      } catch (error) {
        logger.error("Error cancelling queue request", error as Error)
      } finally {
        // The API wait is a race boundary. It may only clear the exact queue id
        // captured at click time; all turn state and ownership may now belong to
        // a newer request and must remain untouched.
        const latestState = readSessionState(sessionIdForCancel)
        if (latestState.queueRequestId === queueRequestIdAtCancel) {
          writeSessionState(sessionIdForCancel, { queueRequestId: null })
        }
      }
      return
    }

    if (!ownerTokenAtCancel) return

    // While the server has announced steer_ready, first Stop asks it to wind
    // down and persist the partial. A second Stop (cancelPending) deliberately
    // falls through to the exact-owner hard-abort path below.
    const steerStopActive =
      ownedRequestControl &&
      steerStopRedirectEnabled &&
      stateAtCancel.steerReady &&
      !stateAtCancel.cancelPending &&
      (stateAtCancel.isLoading || stateAtCancel.isStreaming)
    if (steerStopActive && requestSteerCancel(sessionIdForCancel, ownedRequestControl)) {
      return
    }

    const cancelledOwner = cancelOwnedRequest({
      sessionId: sessionIdForCancel,
      requestToken: ownerTokenAtCancel,
      requestControl: ownedRequestControl,
      state: legacyStateAtCancel,
    })
    if (cancelledOwner) {
      settleSteerAfterHardAbort(sessionIdForCancel, legacyStateAtCancel)
    }
  }

  const handleRemoveFile = (file: File) => {
    removeFile(file)
  }

  const handleAddFiles = (filesToAdd: File[]) => {
    addFiles(filesToAdd)
  }

  const handleResetFiles = () => {
    resetFiles()
  }

  async function getGraphFileAsFile(item: GraphFileItem): Promise<File> {
    const blob = await downloadGraphFileApi(item, settingsService.token)
    const mime = item.mimeType || blob.type || "application/octet-stream"
    return new File([blob], item.name, { type: mime })
  }

  const handleContinue = async () => {
    if (!chatState.lastQuestion[0]) return

    // If no session exists in ChatService, fall back to handleSendQuestion
    // which will create a new session before sending the request
    const session = chatService.getOpenSession(chatSessionType)
    if (!session) {
      await handleSendQuestion(
        getRetryRequestForLastQuestion(chatState.lastQuestion, chatState.lastRequestAgentId)
      )
      return
    }

    changeChatState({
      error: undefined,
      canContinue: false,
    })

    if (aborted) {
      setAborted(undefined)
    }
    if (gptModel?.feature_flags?.supports_streaming ?? true) {
      await sendChatRequest({
        ...getRetryRequestForLastQuestion(chatState.lastQuestion, chatState.lastRequestAgentId),
        isContinuation: false,
        isRetry: true,
      })
    } else {
      // Check if we have a valid last response
      const answersToCheck = chatState.streamingAnswer
        ? [...chatState.messages, chatState.streamingAnswer]
        : chatState.messages
      const lastAnswer = answersToCheck[answersToCheck.length - 1]?.[1]

      // Check if there's valid content
      const hasContent = lastAnswer?.content
        ? getResponseContent(lastAnswer).trim().length > 0
        : false

      if (!hasContent) {
        // Remove streamingAnswer or last message and retry
        changeChatState({
          messages: chatState.messages.slice(0, -1),
          streamingAnswer: null,
        })
        await sendChatRequest({
          ...getRetryRequestForLastQuestion(chatState.lastQuestion, chatState.lastRequestAgentId),
          isContinuation: false,
        })
      } else {
        await sendChatRequest({
          ...getRetryRequestForLastQuestion(chatState.lastQuestion, chatState.lastRequestAgentId),
          isContinuation: true,
        })
      }
    }
  }

  const handleRetry = async () => {
    if (!chatState.lastQuestion[0]) return

    await handleSendQuestion(
      getRetryRequestForLastQuestion(chatState.lastQuestion, chatState.lastRequestAgentId)
    )
  }

  return {
    ensureSession,
    handleExampleClicked,
    handleSendQuestion,
    handleCancelRequest,
    requestForceRedirect,
    handleContinue,
    handleRemoveFile,
    handleAddFiles,
    handleResetFiles,
    sendChatRequest,
    aborted,
    setAborted,
    changeTokenCounts,
    totalTokenCount,
    handleRetry,
    getGraphFileAsFile,
  }
}
